import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Plus, Star, RefreshCcw } from "lucide-react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import WarehouseFormModal from "./WarehouseFormModal";
import "../Accounting/FixedAssets/fixedAssets.css";

export default function Warehouses() {
  const { companyId, companyRole } = useCompany();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    const { data, error } = await supabase
      .from("warehouses")
      .select("id,name,is_default,created_at")
      .eq("company_id", companyId)
      .order("name", { ascending: true });

    if (!error) setRows(data || []);
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const defaultWh = useMemo(() => rows.find((r) => r.is_default), [rows]);

  async function setDefault(id) {
    if (!companyId) return;
    setBusy(true);
    try {
      // single default is enforced by unique partial index;
      // safest approach: unset all then set one
      const { error: e1 } = await supabase
        .from("warehouses")
        .update({ is_default: false })
        .eq("company_id", companyId);

      if (e1) throw e1;

      const { error: e2 } = await supabase
        .from("warehouses")
        .update({ is_default: true })
        .eq("company_id", companyId)
        .eq("id", id);

      if (e2) throw e2;

      await load();
    } catch (e) {
      alert(e.message || "Failed to set default");
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function remove(id) {
    const ok = window.confirm(
      "Delete warehouse? This is blocked if it has inventory balances or movements."
    );
    if (!ok) return;

    setBusy(true);
    try {
      const { error } = await supabase
        .from("warehouses")
        .delete()
        .eq("company_id", companyId)
        .eq("id", id);

      if (error) throw error;
      await load();
    } catch (e) {
      alert(
        e.message ||
          "Failed to delete. Ensure warehouse has no balances/movements."
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fa-wrap">
      <div className="fa-header">
        <div className="fa-title">
          <h2>Warehouses</h2>
          <p>
            Create locations and set one default warehouse. Transfers are
            supported.
          </p>
        </div>

        <div className="fa-actions">
          <button className="fa-btn" onClick={load} disabled={busy}>
            <RefreshCcw size={16} /> Refresh
          </button>
          <button
            className="fa-btn fa-btn-primary"
            onClick={() => {
              setEditRow(null);
              setOpen(true);
            }}
          >
            <Plus size={16} /> Add Warehouse
          </button>
        </div>
      </div>

      <div className="fa-panel">
        <div style={{ opacity: 0.85, marginBottom: 10, fontSize: 13 }}>
          Default:{" "}
          <b>{defaultWh ? `${defaultWh.name}` : "Not set (create one)"}</b>
        </div>

        <table className="fa-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Default</th>
              <th style={{ width: 260 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td style={{ fontWeight: 900 }}>{r.name}</td>
                <td>{r.is_default ? "Yes" : "No"}</td>
                <td style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button
                    className="fa-btn"
                    onClick={() => {
                      setEditRow(r);
                      setOpen(true);
                    }}
                    disabled={busy}
                  >
                    Edit
                  </button>

                  <button
                    className="fa-btn"
                    onClick={() => setDefault(r.id)}
                    disabled={busy || r.is_default}
                    title="Set as default"
                  >
                    <Star size={16} /> Set default
                  </button>

                  {companyRole === "admin" && (
                    <button
                      className="fa-btn"
                      onClick={() => remove(r.id)}
                      disabled={busy || r.is_default}
                      title={
                        r.is_default
                          ? "Cannot delete default warehouse"
                          : "Delete warehouse (admin only)"
                      }
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}

            {rows.length === 0 && (
              <tr>
                <td colSpan={3} style={{ opacity: 0.75 }}>
                  No warehouses yet. Create one (we recommend a default “Main
                  Warehouse”).
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <WarehouseFormModal
          warehouse={editRow}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
}
